import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useThemeContext } from 'shared/context/themeContext';

export const SelectedItemContext = createContext<string | null>(null);

const hasSelectedDescendant = (
  children: React.ReactNode,
  selectedItem: string | null
): boolean => {
  if (!selectedItem) {
    return false;
  }
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) {
      return;
    }
    const childProps = child.props as {
      redirectTo?: string;
      children?: React.ReactNode;
    };
    if (childProps.redirectTo === selectedItem) {
      found = true;
      return;
    }
    if (childProps.children && hasSelectedDescendant(childProps.children, selectedItem)) {
      found = true;
    }
  });
  return found;
};

interface Props {
  icon?: React.ReactNode;
  label: string;
  fontSize?: number;
  abbreviationLabel?: string;
  isSelected?: boolean;
  isOpen?: boolean;
  onChildOpened?: (x: boolean) => void;
  isCollapsed: boolean;
  redirectTo?: string;
  noneIcon?: boolean;
}

const SidebarItem: FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { theme } = useThemeContext();
  const isDark = theme === 'dark';
  const selectedItem = useContext(SelectedItemContext);
  const childIsSelected =
    !props.isSelected && hasSelectedDescendant(props.children, selectedItem);

  useEffect(() => {
    if (props.isOpen !== undefined) {
      setIsOpen(props.isOpen);
    }
  }, [props.isOpen]);

  const LinkComponent = props.redirectTo
    ? Link
    : (props: any) => (
      <div {...props} onClick={() => setIsOpen((open) => !open)} />
    );

  const fontSize = props.fontSize ?? 13;
  const defaultTextClass = isDark
    ? 'text-sidebarText-default hover:text-sidebarText-hover'
    : 'text-blueGray-500 hover:text-secondary-700';
  const selectedTextClass = isDark
    ? 'text-sidebarText-selected'
    : 'text-secondary-700';
  // Mismo color que se usa al hacer hover — un padre con un hijo activo se
  // ve exactamente así, no con un estilo propio distinto.
  const hoverTextClass = isDark
    ? 'text-sidebarText-hover'
    : 'text-secondary-700';
  return (
    <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      className={'border-left-2 border-blue-400 ' + defaultTextClass + ' '}
    >
      <LinkComponent
        className={
          'flex cursor-pointer items-center pr-2 py-2 ' +
          (props.isSelected
            ? selectedTextClass
            : childIsSelected
              ? hoverTextClass
              : '')
        }
        to={props.redirectTo}
      >
        {props.icon ? (
          <>{props.icon}</>
        ) : props.noneIcon ? (
          null) : (
          <FiberManualRecordIcon style={{ width: 10 }} />
        )
        }
        <h1
          className={
            `soft-transition delay-75 opacity-100`
          }
          style={{ marginLeft: '12px', fontSize: (isHovered ? fontSize + 2 : fontSize) + 'px', transition: 'font-size 0.2s ease-in-out' }}
        >
          {props.isCollapsed ? props.abbreviationLabel : props.label}
        </h1>

        {isOpen
          ? props.children && (
            <ExpandMoreIcon
              className={
                'soft-transition delay-75 ' +
                ('opacity-100')
              }
              style={{ width: 20, marginLeft: 'auto' }}
            />
          )
          : props.children && (
            <NavigateNextIcon
              className={
                'soft-transition ' +
                (props.isCollapsed ? 'opacity-0' : 'opacity-100')
              }
              style={{ width: 20, marginLeft: 'auto' }}
            />
          )}
      </LinkComponent>
      <div
        className={`pl-4 overflow-hidden transition-all duration-500 ease-in-out`}
        style={{ maxHeight: isOpen ? 1000 : 0, marginLeft: props.isCollapsed ? '8px' : '24px' }}
      >
        {props.children}
      </div>
    </div>
  );
};

export default SidebarItem;
